/**
 * The disk guard (SANDBOX_DISK_GUARD_V1, 2026-10-02): the volume measured on every lease beat,
 * and three lines on its used fraction, so a box is careful long before it is full.
 *
 * Why. A throwaway box whose volume reached 100% came back COLD on every wake afterwards: the
 * RAM layer wiped, the mounts gone, ~1.25 GB of a pnpm store in `lost+found`. A box that never
 * filled kept its state through every standby (mockups/shared-cache-spike-2026-10-02.html,
 * "a full volume broke the box"). A full user disk is not a soft failure, so the box must
 * never be the one that fills it.
 *
 * THE THREE LINES, on `used / cap` where `used` is what statfs says writers cannot have
 * (`cap - bavail * bsize`, the vitals sample's own number):
 *  - 80%, RECLAIM: the box gives back pure caches, and only caches (an exact catalog,
 *    apps/sandbox services/disk-guard/catalog.ts). It is the line the sweeper's pressure
 *    ladder already opens at (tidy/ladder.ts PRESSURE_OPEN), so "the box starts handing back
 *    rebuildable bytes" means one number on the whole box.
 *  - 90%, ALMOST FULL: the agent is told before it starts a large write, and the beat carries
 *    a plain sentence a person can be shown. Just above the server's grow line (85%,
 *    grow-decision.ts VOLUME_GROW_LINE): a box between the two is one the server is already
 *    trying to grow, and the agent hears only once that has not been enough.
 *  - 95%, FULL: our OWN big writers refuse to start (downloads into the box, an archive
 *    restore), and the server is asked to grow the volume. 5% is the reserve the workspace
 *    head already keeps for a live task
 *    (workspace-head/record.ts NO_ROOM_RESERVE_FRACTION): what is left above this line belongs
 *    to the person's own work, never to ours.
 *
 * A reclaim stops once the volume is back under 70% (the ladder's close line), so it never
 * takes more cache than the pressure asked for.
 *
 * ONE parser for the switch and ONE tier function, read by the runner (the beat, the reclaim,
 * the agent's line, the refusals) and the server (the grow request), so no two places can
 * disagree about where a line is.
 */
import { z } from 'zod';

/** off = nothing; dry = measured and reported, nothing deleted or refused; on = enforced. */
export type DiskGuardMode = 'off' | 'dry' | 'on';

/**
 * `'on'` → on, `'dry'` → dry, ANYTHING else → off. Fail-safe like the sweeper's switch: an
 * unset flag, a typo or a stray `'true'` lands on off, so a box that cannot read its own flag
 * deletes and refuses nothing.
 */
export function parseDiskGuardMode(raw: string | undefined): DiskGuardMode {
  if (raw === 'on') return 'on';
  if (raw === 'dry') return 'dry';
  return 'off';
}

/** Where each tier starts, as a fraction of the volume used. */
export const RECLAIM_LINE = 0.8;
export const ALMOST_FULL_LINE = 0.9;
export const FULL_LINE = 0.95;
/** A reclaim stops once the volume is back under this. */
export const RECLAIM_TARGET = 0.7;

/** The volume's state on the beat: `reclaim` is the box tidying up, nothing a person needs to hear. */
export type DiskState = 'ok' | 'reclaim' | 'almost_full' | 'full';

/** Order of the states, so "did it get worse" is a comparison. */
export const DISK_STATE_RANK: Readonly<Record<DiskState, number>> = { ok: 0, reclaim: 1, almost_full: 2, full: 3 };

/**
 * The state of a volume with `used` of `cap` bytes taken, or undefined when the numbers cannot
 * be a volume's (a zero cap is a sampler that failed, never a full disk). Each line is
 * INCLUSIVE: a volume exactly at 95% is full.
 */
export function diskState(used: number, cap: number): DiskState | undefined {
  if (!(cap > 0) || !(used >= 0) || !Number.isFinite(used) || !Number.isFinite(cap)) return undefined;
  if (used >= cap * FULL_LINE) return 'full';
  if (used >= cap * ALMOST_FULL_LINE) return 'almost_full';
  if (used >= cap * RECLAIM_LINE) return 'reclaim';
  return 'ok';
}

/**
 * Whether writing `bytes` more would take the volume to the full line. One question for every
 * writer of ours: a box already at the line refuses a write of any size, and a known-size write
 * that would cross it refuses before its first byte.
 */
export function crossesFullLine(used: number, cap: number, bytes: number): boolean {
  if (!(cap > 0)) return false;
  return used + Math.max(0, bytes) >= cap * FULL_LINE;
}

const GiB = 1024 ** 3;
const MiB = 1024 ** 2;

const oneDecimal = (n: number): string => {
  const t = n.toFixed(1);
  return t.endsWith('.0') ? t.slice(0, -2) : t;
};

/** "7.8", "8": a volume's size in GB, rounded to one place, a whole number without its ".0". */
export function gbText(bytes: number): string {
  return oneDecimal(Math.round((bytes / GiB) * 10) / 10);
}

/**
 * What is used, in GB to one place, rounded DOWN: a volume 99.6% used reads "7.9 of 8 GB", never
 * "8 of 8 GB", which would say there is no room at all when there is some.
 */
export function usedGbText(bytes: number): string {
  return oneDecimal(Math.floor((bytes / GiB) * 10 + 1e-9) / 10);
}

/** A free amount in words: "450 MB" under a GB (where "0.4 GB" says too little), else "1.2 GB". */
export function sizeText(bytes: number): string {
  return bytes < GiB ? `${Math.floor(Math.max(0, bytes) / MiB)} MB` : `${usedGbText(bytes)} GB`;
}

/**
 * The calm, plain sentence a person can be shown, or undefined when there is nothing to say.
 * Only the two states a person should hear about have one: a box tidying its own caches is
 * not news.
 */
export function diskHealthLine(state: DiskState, used: number, cap: number): string | undefined {
  if (state === 'almost_full') return `Your computer is almost full (${usedGbText(used)} of ${gbText(cap)} GB).`;
  if (state === 'full') return `Your computer is full (${usedGbText(used)} of ${gbText(cap)} GB).`;
  return undefined;
}

const bytes = z.number().int().min(0);

/**
 * The volume's state as it rides the lease beat (`disk`), present while the guard is on or dry.
 * `line` is the sentence above, for the server and the web to show as they choose (a follow-up);
 * `dry` says the guard is only watching, so nothing was deleted or refused and the server must
 * not act on it; `reclaimedBytes` is what the cache reclaim freed (or, dry, would have freed)
 * since the last beat that carried it.
 */
export const DiskHealthSchema = z.object({
  state: z.enum(['ok', 'reclaim', 'almost_full', 'full']),
  usedBytes: bytes,
  capBytes: bytes,
  line: z.string().max(200).optional(),
  dry: z.boolean().optional(),
  reclaimedBytes: bytes.optional(),
});
export type DiskHealth = z.infer<typeof DiskHealthSchema>;
